import * as helios from "@hyperionbt/helios";
import cx from "clsx";
import { head } from "common-utils/utils/array";
import { Artwork, AuctionBid } from "kreate-common/modules/business-types";
import { assert$DisplayableError } from "kreate-common/modules/displayable-error";
import ButtonOutline from "kreate-common/modules/kreate-ui/components/ButtonOutline";
import ButtonPrimary from "kreate-common/modules/kreate-ui/components/ButtonPrimary";
import CircularProgress$Indeterminate from "kreate-common/modules/kreate-ui/components/CircularProgress/components/CircularProgress$Indeterminate";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { CLIENT_ENV } from "kreate-env/client";
import { buildTx$BidAuction } from "kreate-protocol/offchain/tx/bidAuction";
import { httpGet$GetProtocolParams } from "kreate-server/commands/GetProtocolParams/fetcher";
import { httpGet$GetTxStatus } from "kreate-server/commands/GetTxStatus/fetcher";
import moment from "moment";
import Link from "next/link";
import React from "react";
import { MdFavorite, MdOpenInNew } from "react-icons/md";

import InputLovelaceAmount$Compact from "../../../../components/InputLovelaceAmount$Compact";
import LabelledValue from "../../../../components/LabelledValue";
import PanelLayout$Emphasis from "../../../../components/PanelLayout$Emphasis";

import BidderViewer from "./components/BidderViewer";
import TableBid from "./components/TableBid";
import styles from "./index.module.scss";
import { getAuctionOverview } from "./utils";

import { getAddressFromCip30Wallet } from "@/modules/cardano-wallet/utils";
import { useBusyCount } from "@/modules/common-hooks/useBusyCount";
import { useIsExpired } from "@/modules/common-hooks/useIsExpired";
import { useIsGonnaVisible } from "@/modules/common-hooks/useIsGonnaVisible";
import { waitUntil } from "@/modules/common-utils/async";
import {
  formatLovelaceAmount$Compact,
  formatLovelaceAmount$Standard,
} from "@/modules/common-utils/format";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { EM_DASH } from "@/modules/common-utils/unicode";
import { InlineError } from "@/modules/form-field/errors";
import { useField$LovelaceAmount } from "@/modules/form-field/hooks/useField$LovelaceAmount";
import CountdownTimer from "@/modules/kreate-components/components/CountdownTimer";
import ErrorViewer from "@/modules/kreate-components/components/ErrorViewer";
import InlineErrorViewer from "@/modules/kreate-components/components/InlineErrorViewer";
import { useModalTrigger$ModalConnectWallet } from "@/modules/kreate-modals/modals/ModalConnectWallet/hooks/useModalTrigger$ModalConnectWallet";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";
import { useModalTrigger$ModalProgress } from "@/modules/kreate-modals/modals/ModalProgress/hooks/useModalTrigger$ModalProgress";
import { useModalTrigger$ModalSuccess } from "@/modules/kreate-modals/modals/ModalSuccess/hooks";
import { useModalTrigger$ModalTransactionBreakdown } from "@/modules/kreate-modals/modals/ModalTransactionBreakdown/hooks";
import { useAuctionBidListInfinite } from "@/modules/resources/hooks/useAuctionBidListInfinite";

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

type Props = {
  className?: string;
  style?: React.CSSProperties;
  artwork: Artwork;
};

export default function MethodAuctionEnglish({
  className,
  style,
  artwork,
}: Props) {
  const { busy, withCustomErrorHandler } = useBusyCount();

  const modalTriggers = {
    ModalProgress: useModalTrigger$ModalProgress(),
    ModalSuccess: useModalTrigger$ModalSuccess(),
    ModalTransactionBreakdown: useModalTrigger$ModalTransactionBreakdown(),
    ModalConnectWallet: useModalTrigger$ModalConnectWallet(),
    ModalFailure: useModalTrigger$ModalFailure(),
  };

  const overview = getAuctionOverview(artwork);

  const swr = useAuctionBidListInfinite(
    overview?.assetId ? { assetId: overview.assetId, limit: 12 } : undefined
  );

  const [buttonLoadMoreEl, setButtonLoadMoreEl] =
    React.useState<Element | null>(null);
  const isButtonLoadMoreGonnaBeVisible = useIsGonnaVisible({
    element: buttonLoadMoreEl,
    rootMargin: "0px",
  });

  const shouldLoadMoreNow =
    isButtonLoadMoreGonnaBeVisible &&
    !swr.computed.isLoadingMore &&
    !swr.computed.isEnded &&
    !!swr.loadMore;

  React.useEffect(() => {
    if (shouldLoadMoreNow) {
      swr.loadMore?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoadMoreNow]);

  const leadingBid: AuctionBid | undefined = head(head(swr.data)?.auctionBids);

  const fieldBidAmount = useField$LovelaceAmount({
    initialValue: undefined,
    min: !overview
      ? 0
      : leadingBid
      ? leadingBid.bidAmount + overview.increment
      : overview.reserve,
    max: 1e12,
  });

  const isStarted = useIsExpired(overview?.startedAt);
  const isEnded = useIsExpired(overview?.endedAt);

  const handleError = async (error: unknown) => {
    if (error instanceof InlineError) return;
    await modalTriggers.ModalFailure.openModalWithError(error);
  };

  const placeBid = withCustomErrorHandler(handleError)(async () => {
    const bidAmount = fieldBidAmount.validateBeforeSubmitting();

    const cip30Wallet =
      await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();

    await modalTriggers.ModalProgress.openModal$AsyncFunction(
      async (controller) => {
        controller.update({
          text: "Fetching protocol params...",
          ratio: 0.0,
          ratioUpTo: 0.1,
          shrinkRatio: 0.9375,
        });

        const [protocolParamsUTxO, address] = await Promise.all([
          httpGet$GetProtocolParams("/api/v1/utxo/protocol-params"),
          getAddressFromCip30Wallet(cip30Wallet),
        ]);

        assert$DisplayableError(
          protocolParamsUTxO.datum.type === "protocol-params",
          "Invalid protocol params"
        );
        assert$DisplayableError(artwork.utxo, "Invalid artwork UTxO");

        controller.update({
          text: "Building transaction...",
          ratio: 0.1,
          ratioUpTo: 0.2,
          shrinkRatio: 0.9375,
        });

        const { tx, bidFee, txFee, isSelfOutbidding } =
          await buildTx$BidAuction({
            artworkTitle: artwork.title,
            network: CLIENT_ENV.CARDANO_NETWORK,
            wallet: cip30Wallet,
            treasuryAddress: new helios.Address(
              protocolParamsUTxO.datum.value.treasuryAddress
            ),
            auctionUTxO: artwork.utxo,
            bidderAddress: new helios.Address(address),
            newBidAmount: BigInt(bidAmount),
          });

        const confirmed =
          await modalTriggers.ModalTransactionBreakdown.openModal(
            [
              {
                label: "Bid",
                lovelaceAmount: Number(bidFee),
                tooltip: isSelfOutbidding
                  ? `You are raising your bid to ${formatLovelaceAmount$Standard(
                      bidAmount
                    )}.`
                  : "The amount of ADA you’ve pledged to purchase the Artwork with in this Auction. If someone outbids you, your pledge will be returned to your wallet.",
              },
              {
                label: "Transaction Fee",
                lovelaceAmount: Number(txFee),
                tooltip:
                  "The blockchain network fees to process this transaction",
              },
            ],
            { artwork }
          );

        if (!confirmed) {
          throw null;
        }

        controller.update({
          text: "Waiting for signature and submission...",
          ratio: 0.2,
          ratioUpTo: 0.3,
          shrinkRatio: 0.9375,
        });

        const signatures = await cip30Wallet.signTx(tx);
        tx.addSignatures(signatures);

        const txId = await cip30Wallet.submitTx(tx).then((h) => h.hex);

        controller.update({
          text: "Waiting for confirmation...",
          ratio: 0.3,
          ratioUpTo: 1.0,
          shrinkRatio: 0.9375,
        });

        await waitUntil(async () => {
          const status = await httpGet$GetTxStatus(`/api/v1/tx-status/${txId}`);
          return status.ok;
        });

        await shamelesslyRevalidateEverything();
      }
    );

    await modalTriggers.ModalSuccess.openModal({
      icon: <MdFavorite />,
      content: "Your bid has been placed.",
      buttons: [{ content: "Close", onClick: "close" }],
    });
  });

  if (!overview) {
    return null;
  }

  return (
    <div className={cx(styles.container, className)} style={style}>
      <PanelLayout$Emphasis.Root>
        <PanelLayout$Emphasis.Top>
          {isEnded == null ? (
            <Flex.Row
              padding="24px"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress$Indeterminate
                className={styles.colorPrimary}
                size="24px"
              />
            </Flex.Row>
          ) : isEnded ? (
            <Flex.Row
              gap="8px"
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems="center"
            >
              <Flex.Cell className={styles.textAuctionClosed}>
                {"Auction Ended"}
              </Flex.Cell>
              <Flex.Row gap="8px" alignItems="center">
                <Flex.Cell className={styles.textAuctionWinner} flex="0 0 auto">
                  {"Auction Winner:"}
                </Flex.Cell>
                {!leadingBid ? (
                  <span>{EM_DASH}</span>
                ) : (
                  <BidderViewer
                    className={styles.auctionWinnerAddress}
                    address={leadingBid.bidderAddress}
                    user={leadingBid.populated?.userProfile}
                    adaHandles={leadingBid.adaHandles}
                  />
                )}
              </Flex.Row>
            </Flex.Row>
          ) : isStarted == null ? (
            <Flex.Row
              padding="24px"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress$Indeterminate
                className={styles.colorPrimary}
                size="24px"
              />
            </Flex.Row>
          ) : isStarted ? (
            <Flex.Row
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap="8px"
            >
              <Flex.Row
                flexWrap="wrap"
                gap="8px"
                alignItems="center"
                flex="0 0 auto"
              >
                <div className={styles.textBiddingClosesIn}>
                  {"Auction closes in"}
                </div>
                <div className={styles.countdownTimer}>
                  <CountdownTimer
                    as="span"
                    expiredAt={overview?.endedAt}
                    unstyled
                  />
                </div>
              </Flex.Row>
              {leadingBid ? (
                <Flex.Row
                  gap="8px"
                  alignItems="center"
                  flex="0 1 auto"
                  minWidth="0"
                >
                  <Flex.Cell className={styles.textLeader}>
                    {"Leader"}
                  </Flex.Cell>
                  <Flex.Cell flex="0 1 auto" minWidth="0" asChild>
                    <BidderViewer
                      className={styles.auctionLeaderAddress}
                      address={leadingBid.bidderAddress}
                      user={leadingBid.populated?.userProfile}
                      adaHandles={leadingBid.adaHandles}
                    />
                  </Flex.Cell>
                </Flex.Row>
              ) : undefined}
            </Flex.Row>
          ) : (
            <Flex.Row gap="8px" flexWrap="wrap" alignItems="center">
              <div className={styles.textComingSoon}>{"Coming soon:"}</div>
              <div className={styles.countdownTimer}>
                <CountdownTimer
                  as="span"
                  expiredAt={overview?.startedAt}
                  unstyled
                />
              </div>
            </Flex.Row>
          )}
        </PanelLayout$Emphasis.Top>
        <PanelLayout$Emphasis.Main>
          <Flex.Col gap="20px">
            <Flex.Row
              justifyContent="space-between"
              alignItems="flex-start"
              gap="24px"
              flexWrap="wrap"
            >
              <LabelledValue
                label="Highest Bid"
                value={
                  <span className={!isEnded ? styles.colorPrimary : undefined}>
                    {formatLovelaceAmount$Compact(leadingBid?.bidAmount)}
                  </span>
                }
                title={formatLovelaceAmount$Standard(leadingBid?.bidAmount)}
              />
              <LabelledValue
                label="Reserve"
                value={formatLovelaceAmount$Compact(overview.reserve)}
                title={formatLovelaceAmount$Standard(overview.reserve)}
              />
              <LabelledValue
                label="Min Increment"
                value={formatLovelaceAmount$Compact(overview.increment)}
                title={formatLovelaceAmount$Standard(overview.increment)}
              />
              <LabelledValue
                label="Edition"
                value={`${overview.numAuctionedEditions || EM_DASH}/${
                  artwork.totalSupply || EM_DASH
                }`}
              />
            </Flex.Row>
            {!isEnded && isStarted ? (
              <Flex.Row justifyContent="stretch" gap="8px" flexWrap="wrap">
                <Flex.Cell minWidth="262px" flex="1 1 auto" asChild>
                  <InputLovelaceAmount$Compact.UseField$LovelaceAmount
                    api={fieldBidAmount}
                    placeholder="Your Bid"
                  />
                </Flex.Cell>
                <Flex.Cell flex="10000 10000 auto" minWidth="0" asChild>
                  <ButtonPrimary
                    size="lg"
                    content="Place a Bid"
                    onClick={placeBid}
                    disabled={busy}
                  />
                </Flex.Cell>
              </Flex.Row>
            ) : undefined}
            {fieldBidAmount.error ? (
              <InlineErrorViewer content={fieldBidAmount.error.message} />
            ) : undefined}
            {swr.error ? <ErrorViewer error={swr.error} /> : undefined}
          </Flex.Col>
        </PanelLayout$Emphasis.Main>
        <PanelLayout$Emphasis.Bottom>
          <TableBid.Root>
            {!swr.computed.isEmpty ? (
              <TableBid.Head>
                <TableBid.Row>
                  <TableBid.Cell content="Price" />
                  <TableBid.Cell content="Time" />
                  <TableBid.Cell content="Bidder" />
                  <TableBid.Cell content="Tx" />
                </TableBid.Row>
              </TableBid.Head>
            ) : undefined}
            <TableBid.Body>
              {swr.data?.map((page) =>
                page.auctionBids.map((bid) => (
                  <TableBid.Row key={bid.placedAt}>
                    <TableBid.Cell
                      content={formatLovelaceAmount$Compact(bid.bidAmount)}
                    />
                    <TableBid.Cell
                      content={
                        bid.placedAt + MILLISECONDS_PER_DAY < Date.now()
                          ? moment(bid.placedAt).format("MM/DD HH:mm")
                          : moment(bid.placedAt).fromNow()
                      }
                      title={moment(bid.placedAt).format()}
                    />
                    <TableBid.Cell>
                      <BidderViewer
                        address={bid.bidderAddress}
                        user={bid.populated?.userProfile}
                        adaHandles={bid.adaHandles}
                      />
                    </TableBid.Cell>
                    <TableBid.Cell>
                      <Link
                        href={`https://cexplorer.io/tx/${bid.txHash}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <MdOpenInNew size="24px" />
                      </Link>
                    </TableBid.Cell>
                  </TableBid.Row>
                ))
              )}
              {swr.computed.isLoadingMore ? (
                <TableBid.Row className={styles.textNoBidsYet}>
                  <Flex.Row
                    flex="1 1 auto"
                    minWidth="0"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <CircularProgress$Indeterminate
                      className={styles.colorPrimary}
                      size="48px"
                    />
                  </Flex.Row>
                </TableBid.Row>
              ) : swr.computed.isEmpty ? (
                <TableBid.Row className={styles.textNoBidsYet}>
                  <Flex.Cell flex="1 1 auto" minWidth="0" padding="32px">
                    {"No bids yet"}
                  </Flex.Cell>
                </TableBid.Row>
              ) : swr.computed.isEnded ? (
                <TableBid.Row className={styles.textNoMoreBids}>
                  <Flex.Cell flex="1 1 auto" minWidth="0" padding="16px">
                    {"No more bids"}
                  </Flex.Cell>
                </TableBid.Row>
              ) : (
                <TableBid.Row>
                  <Flex.Row
                    flex="1 1 auto"
                    minWidth="0"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <ButtonOutline
                      ref={setButtonLoadMoreEl}
                      content="Load More"
                      disabled={!swr.loadMore}
                      onClick={swr.loadMore}
                    />
                  </Flex.Row>
                </TableBid.Row>
              )}
            </TableBid.Body>
          </TableBid.Root>
        </PanelLayout$Emphasis.Bottom>
      </PanelLayout$Emphasis.Root>
    </div>
  );
}
